import { defineStore } from 'pinia'

const CART_STORAGE_KEY = 'cart_items'

function safeParseCartStorage() {
  try {
    const parsed = JSON.parse(localStorage.getItem(CART_STORAGE_KEY) || '{}')
    return Array.isArray(parsed?.events) ? parsed.events : []
  } catch {
    localStorage.removeItem(CART_STORAGE_KEY)
    return []
  }
}

function normalizeQuantity(quantity) {
  const value = Number(quantity)
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 1
}

function normalizeStoredEvents(events) {
  return events
    .map(event => ({
      id: String(event?.id || ''),
      items: Array.isArray(event?.items)
        ? event.items
            .map(item => ({
              id: String(item?.id || ''),
              quantity: normalizeQuantity(item?.quantity),
            }))
            .filter(item => item.id)
        : [],
    }))
    .filter(event => event.id && event.items.length > 0)
}

function normalizeCatalogItem(item, quantity) {
  return {
    ...item,
    photo_url: item.photo_url || item.imageUrl || '',
    quantity: normalizeQuantity(quantity),
  }
}

function persistMinimalEvents(events) {
  const minimalEvents = normalizeStoredEvents(events)
  localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({ events: minimalEvents }))
}

const initialEvents = normalizeStoredEvents(safeParseCartStorage())
persistMinimalEvents(initialEvents)

export const useCartStore = defineStore('cart', {
  state: () => ({
    events: initialEvents
  }),

  getters: {
    // Busca um evento pelo ID
    getEvent: (state) => (eventId) => {
      return state.events.find(e => String(e.id) === String(eventId))
    },

    // Retorna todos os itens de um evento
    getItemsByEvent: (state) => (eventId) => {
      const event = state.events.find(e => String(e.id) === String(eventId))
      return event ? event.items : []
    },

    // Verifica se um item está presente no evento
    hasItem: (state) => (eventId, itemId) => {
      const event = state.events.find(e => String(e.id) === String(eventId))
      return event?.items.some(item => String(item.id) === String(itemId)) || false
    },

    // Retorna o total de itens de um evento
    itemCount: (state) => (eventId) => {
      const event = state.events.find(e => String(e.id) === String(eventId))
      return event?.items.length || 0
    },

    // Soma total de quantidades em todos os eventos
    totalQuantity: (state) => {
      return state.events.reduce((acc, ev) => {
        return acc + ev.items.reduce((sum, item) => sum + item.quantity, 0)
      }, 0)
    }
  },

  actions: {

    clearEvent(eventId) {
      const index = this.events.findIndex(e => String(e.id) === String(eventId))
      if (index !== -1) {
        this.events.splice(index, 1)
        this.events = [...this.events]
        this.saveToStorage()
      }

      localStorage.removeItem(`pixData-${eventId}`)
    },

    hydrateEvent(eventData) {
      if (!eventData?.id || !Array.isArray(eventData?.items)) return

      const eventIndex = this.events.findIndex(e => String(e.id) === String(eventData.id))
      if (eventIndex === -1) return

      const catalog = new Map(eventData.items.map(item => [String(item.id), item]))
      const hydratedItems = this.events[eventIndex].items
        .map(item => {
          const catalogItem = catalog.get(String(item.id))
          return catalogItem ? normalizeCatalogItem(catalogItem, item.quantity) : null
        })
        .filter(Boolean)

      if (hydratedItems.length > 0) {
        this.events[eventIndex] = {
          id: String(eventData.id),
          name: eventData.name,
          status: eventData.status,
          items: hydratedItems,
        }
      } else {
        this.events.splice(eventIndex, 1)
      }

      this.events = [...this.events]
      this.saveToStorage()
    },

    addItem(eventData, item) {
      const eventIndex = this.events.findIndex(e => String(e.id) === String(eventData.id))
      const cartItem = normalizeCatalogItem(item, item.quantity)

      if (eventIndex === -1) {
        // Evento ainda não existe
        this.events.push({
          id: String(eventData.id),
          name: eventData.name,
          status: eventData.status,
          items: [cartItem]
        })
      } else {
        const existingEvent = this.events[eventIndex]
        const exists = existingEvent.items.some(i => String(i.id) === String(item.id))

        if (!exists) {
          // reatribuir o array de itens para forçar reatividade
          const updatedItems = [...existingEvent.items, cartItem]
          this.events[eventIndex] = {
            ...existingEvent,
            id: String(eventData.id),
            name: eventData.name,
            status: eventData.status,
            items: updatedItems
          }

          // reatribuir o array de eventos também
          this.events = [...this.events]
        }
      }

      this.saveToStorage()
    },

    removeItem(eventId, itemId) {
      const eventIndex = this.events.findIndex(e => String(e.id) === String(eventId))
      if (eventIndex !== -1) {
        const updatedItems = this.events[eventIndex].items.filter(i => String(i.id) !== String(itemId))

        if (updatedItems.length > 0) {
          this.events[eventIndex] = {
            ...this.events[eventIndex],
            items: updatedItems
          }
        } else {
          this.events.splice(eventIndex, 1)
        }

        // reatribuir array completo para garantir atualização reativa
        this.events = [...this.events]
        this.saveToStorage()
      }
    },

    toggleItem(eventData, item) {
      if (this.hasItem(eventData.id, item.id)) {
        this.removeItem(eventData.id, item.id)
      } else {
        this.addItem(eventData, item)
      }
    },

    clear() {
      this.events = []
      this.saveToStorage()
    },

    saveToStorage() {
      persistMinimalEvents(this.events)
    }
  }
})
